import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import analyticsService from '../../../../services/analyticsService';
import type { KcStatusTimelineEntry, KcStatusTimelineStatus, LearningAlertDetail, PrerequisiteInfo } from '../../../../types/radar';
import { formatTimeOnly } from '../../../../utils/formatUtils';
import { formatLocations } from '../../../../utils/radarUtils';
import { getMockLearningAlertDetails } from '../../mockRadarData';

type UseLearningBlockContentProps = {
  annotationId: string;
  isMockMode?: boolean;
};

type TimelineItem = {
  dateLabel: string;
  id: string;
  isLast: boolean;
  isToday: boolean;
  status: KcStatusTimelineStatus;
  statusLabelKey: string;
  timeLabel: string;
};

type UseLearningBlockContentReturn = {
  detail: LearningAlertDetail | null;
  error: string | null;
  handleToggleRollbackInfo: () => void;
  handleToggleTimeline: () => void;
  hasRollbacks: boolean;
  hasTimeline: boolean;
  isLoading: boolean;
  isRollbackInfoExpanded: boolean;
  isTimelineExpanded: boolean;
  locationItems: string[];
  t: ReturnType<typeof useTranslation>['t'];
  timelineItems: TimelineItem[];
};

function formatPercentage(value: number): string {
  return `${Math.round(value * 100)}%`;
}

function getPrerequisiteColorClass(status: PrerequisiteInfo['status']): string {
  if (status === 'MASTERED') return 'mastered';
  return 'reviewing';
}

function getPrerequisiteStatusKey(status: PrerequisiteInfo['status']): string {
  if (status === 'MASTERED') return 'radar_detail_prereq_mastered';
  return 'radar_detail_prereq_rollback';
}

const TIMELINE_STATUS_KEYS: Record<KcStatusTimelineStatus, string> = {
  MASTERED: 'radar_detail_kc_timeline_status_mastered',
  NEEDS_PROOF: 'radar_detail_kc_timeline_status_needs_proof',
  ROLLBACK: 'radar_detail_kc_timeline_status_rollback',
  ROLLBACK_RESOLVED: 'radar_detail_kc_timeline_status_rollback_resolved',
};

function getTimelineStatusKey(status: KcStatusTimelineStatus): string {
  return TIMELINE_STATUS_KEYS[status];
}

function isSameDay(date: Date, reference: Date): boolean {
  return date.getFullYear() === reference.getFullYear() && date.getMonth() === reference.getMonth() && date.getDate() === reference.getDate();
}

function formatTimelineDate(dateString: string): string {
  const date = new Date(dateString);
  const day = date.getDate();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
}

function useLearningBlockContent({ annotationId, isMockMode }: UseLearningBlockContentProps): UseLearningBlockContentReturn {
  const { i18n, t } = useTranslation(['chatbot']);
  const [detail, setDetail] = useState<LearningAlertDetail | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isRollbackInfoExpanded, setIsRollbackInfoExpanded] = useState<boolean>(false);
  const [isTimelineExpanded, setIsTimelineExpanded] = useState<boolean>(false);

  useEffect(() => {
    if (!annotationId) return;

    if (isMockMode) {
      const mockDetail = getMockLearningAlertDetails(i18n.language)[annotationId] ?? null;
      setDetail(mockDetail);
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    async function fetchDetail(): Promise<void> {
      setIsLoading(true);
      setError(null);

      try {
        const data = await analyticsService.getAnnotationDetail(annotationId);
        if (!cancelled) {
          setDetail(data);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Unknown error');
          setDetail(null);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    fetchDetail();

    return () => {
      cancelled = true;
    };
  }, [annotationId, isMockMode]);

  const handleToggleRollbackInfo = useCallback(() => {
    setIsRollbackInfoExpanded((prev) => !prev);
  }, []);

  const handleToggleTimeline = useCallback(() => {
    setIsTimelineExpanded((prev) => !prev);
  }, []);

  const hasRollbacks = detail ? detail.rollbackCount > 0 : false;
  const locationItems = useMemo(() => (detail ? formatLocations(detail.locations) : []), [detail]);

  const hasTimeline = (detail?.kcStatusTimeline.length ?? 0) > 0;

  const timelineItems = useMemo<TimelineItem[]>(() => {
    const entries: KcStatusTimelineEntry[] = detail?.kcStatusTimeline ?? [];
    if (entries.length === 0) return [];

    const today = new Date();
    const lastIndex = entries.length - 1;

    return entries.map((entry, index) => {
      const entryDate = new Date(entry.createdAt);
      const isToday = isSameDay(entryDate, today);

      return {
        dateLabel: isToday ? t('chatbot:radar_detail_kc_timeline_today') : formatTimelineDate(entry.createdAt),
        id: `${entry.createdAt}-${entry.status}-${index}`,
        isLast: index === lastIndex,
        isToday,
        status: entry.status,
        statusLabelKey: getTimelineStatusKey(entry.status),
        timeLabel: formatTimeOnly(entry.createdAt),
      };
    });
  }, [detail, t]);

  return {
    detail,
    error,
    handleToggleRollbackInfo,
    handleToggleTimeline,
    hasRollbacks,
    hasTimeline,
    isLoading,
    isRollbackInfoExpanded,
    isTimelineExpanded,
    locationItems,
    t,
    timelineItems,
  };
}

export { formatPercentage, getPrerequisiteColorClass, getPrerequisiteStatusKey, getTimelineStatusKey };
export default useLearningBlockContent;
